/**
 * @module composables/useLogin
 *
 * Composable for managing authentication state. Provides reactive refs
 * for JWT tokens and user info, with persistence to localStorage.
 *
 * All refs are module-scoped singletons so auth state is shared across
 * the entire application. This composable is used by:
 * - api/config.js (request interceptor, token refresh, redirect-to-login)
 * - services/logout.js (clearing auth state on logout)
 * - router/navigationGuard.js (checking if user is logged in / admin)
 * - Various components (showing/hiding UI based on auth state)
 */

import { ref, computed } from "vue";

/** JWT access token (short-lived, 15min). Initialized from localStorage. */
const accessToken = ref(localStorage.getItem("accessToken") || null);

/** JWT refresh token (long-lived, 7 days). Initialized from localStorage. */
const refreshToken = ref(localStorage.getItem("refreshToken") || null);

/** User info object { id, email, firstName, lastName, role }. Parsed from localStorage. */
const storedUser = localStorage.getItem("user");
const user = ref(storedUser ? JSON.parse(storedUser) : null);

/** Computed boolean: true if an access token exists (user is authenticated) */
const loggedIn = computed(() => {
  return !!accessToken.value;
});

/** Computed boolean: true if the current user has the "admin" role */
const isAdmin = computed(() => {
  return user.value?.role === "admin";
});

/** Whether the app is running in development mode (based on VITE_APP_NODE_ENV) */
const isDev = ref(import.meta.env.VITE_APP_NODE_ENV === "development");

/**
 * Store user info in both the reactive ref and localStorage.
 * Pass null to clear the user info (used during logout).
 *
 * @param {Object|null} userInfo - User object from API { id, email, firstName, lastName, role }
 */
function setUser(userInfo) {
  user.value = userInfo;
  if (userInfo) {
    localStorage.setItem("user", JSON.stringify(userInfo));
  } else {
    localStorage.removeItem("user");
  }
}

/**
 * Store JWT tokens in both reactive refs and localStorage.
 * Pass null for either token to clear it (used during logout or token refresh failure).
 *
 * @param {string|null} access - The new access token (or null to clear)
 * @param {string|null} refresh - The new refresh token (or null to clear)
 */
function setTokens(access, refresh) {
  accessToken.value = access;
  refreshToken.value = refresh;

  if (access) {
    localStorage.setItem("accessToken", access);
  } else {
    localStorage.removeItem("accessToken");
  }

  if (refresh) {
    localStorage.setItem("refreshToken", refresh);
  } else {
    localStorage.removeItem("refreshToken");
  }
}

/**
 * Get the current access token value.
 *
 * @returns {string|null} The access token or null if not authenticated
 */
function getAccessToken() {
  return accessToken.value;
}

/**
 * Get the current refresh token value.
 *
 * @returns {string|null} The refresh token or null if not set
 */
function getRefreshToken() {
  return refreshToken.value;
}

/**
 * Check if using JWT authentication.
 * Always returns true since the legacy API key auth has been removed.
 *
 * @returns {boolean} Always true
 */
function isJwtAuth() {
  return true;
}

/**
 * Build an authorization headers object for manual API requests.
 * Returns an object with the Authorization Bearer header if a token exists.
 *
 * @returns {Object} Headers object, e.g. { Authorization: "Bearer xxx" }
 */
function getAuthHeaders() {
  const headers = {};

  if (accessToken.value) {
    headers["Authorization"] = `Bearer ${accessToken.value}`;
  }

  return headers;
}

/**
 * Clear all login state: reactive refs and localStorage entries.
 * Called during logout and when auth redirect occurs.
 */
function logout() {
  user.value = null;
  accessToken.value = null;
  refreshToken.value = null;

  localStorage.removeItem("user");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
}

/**
 * Clear only JWT tokens (not user info).
 * Used when a token refresh fails but we want to preserve user data
 * for display purposes until the redirect to login completes.
 */
function clearJwtTokens() {
  accessToken.value = null;
  refreshToken.value = null;

  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
}

/**
 * Composable function returning all authentication state and functions.
 *
 * @returns {Object} Auth state refs, computed properties, and action functions
 */
export function useLogin() {
  return {
    // State
    loggedIn,
    isAdmin,
    isDev,
    user,
    accessToken,
    refreshToken,

    // Actions
    setUser,
    setTokens,
    getAccessToken,
    getRefreshToken,
    getAuthHeaders,
    isJwtAuth,
    clearJwtTokens,
    logout,
  };
}
